/**
 * `/answer/library` — approved-answer library (M-ANSWER.3).
 *
 * Read-only-plus-delete surface. Promotion happens from the questionnaire
 * detail page when an operator approves a response; the library page lists
 * the resulting entries and lets operators remove stale ones.
 */
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAnswerLibrary, useDeleteAnswerLibrary } from "@/hooks/use-answer";
import { formatTimestamp } from "@/lib/formatters";

export function AnswerLibraryPage() {
  const list = useAnswerLibrary();
  const deleteMut = useDeleteAnswerLibrary();

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Answer</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Answer library</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Approved answers that the AI can short-circuit to when a future question is similar
          (cosine match ≥ 0.92). Promote entries from the questionnaire review UI; remove stale
          ones here.
        </p>
      </header>

      {list.isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}
      {list.error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">
              Couldn't load library: {list.error.message}
            </p>
          </CardContent>
        </Card>
      )}
      {list.data && list.data.items.length === 0 && !list.isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>Library is empty</CardTitle>
            <CardDescription>
              Approve a draft response on any questionnaire, then click "Promote to library" to
              add the first entry. Future similar questions auto-fill from the library.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      {list.data && list.data.items.length > 0 && (
        <ul className="grid grid-cols-1 gap-3">
          {list.data.items.map((entry) => (
            <li key={entry.id}>
              <Card>
                <CardContent className="flex items-start justify-between gap-3 pt-6">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Question
                    </p>
                    <p className="mt-1 text-sm text-gray-800">
                      {entry.question_normalized || "(empty)"}
                    </p>
                    <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Approved answer
                    </p>
                    <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-800">
                      {entry.approved_answer}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="outline">used {entry.use_count}×</Badge>
                      <span>created {formatTimestamp(entry.created_at)}</span>
                      {entry.last_used_at && (
                        <span>· last used {formatTimestamp(entry.last_used_at)}</span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteMut.mutate(entry.id)}
                    disabled={deleteMut.isPending}
                  >
                    Delete
                  </Button>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
